import { useEffect, useState } from 'react';
import type { InviteInfo } from '../../../shared/protocol.ts';
import { isEmail } from '../lib/account.ts';
import { api, checkClipUrl } from '../lib/api.ts';
import { formatBytes, formatRate } from '../lib/emitter.ts';
import type { StudioEngine } from '../lib/studio.ts';
import { Avatar, CopyField, Icon, roleLabel } from './common.tsx';

/** Host/producer view of everyone's recording health, plus results of "Check everyone". */
export function TeamPanel({ engine }: { engine: StudioEngine }) {
  const peers = [...engine.peers.values()];
  const links = engine.studio?.links;
  const clipsByCheck = new Map<string, typeof engine.checkClips>();
  for (const c of engine.checkClips) clipsByCheck.set(c.checkId, [...(clipsByCheck.get(c.checkId) ?? []), c]);
  const latest = [...clipsByCheck.values()][0];

  return (
    <>
      {links?.guest && (
        <div className="col" style={{ gap: 6 }}>
          <span className="eyebrow">Invite guests</span>
          <CopyField value={location.origin + links.guest} label="Guest link" />
          {engine.role === 'host' && links.producer && (
            <>
              <span className="faint small">Producers can also start recordings and download files:</span>
              <CopyField value={location.origin + links.producer} label="Producer link" />
            </>
          )}
        </div>
      )}
      {engine.role === 'host' && <InviteByEmail engine={engine} />}

      <div className="col" style={{ gap: 8 }}>
        <span className="eyebrow">In the studio{peers.length ? ` · ${peers.length + 1}` : ''}</span>
        {!peers.length && (
          <div className="empty">
            <Icon name="users" size={22} />
            Nobody else is here yet. Send the guest link above.
          </div>
        )}
        {peers.map((p) => {
          const s = p.status;
          const meshPeer = engine.mesh?.peers.get(p.id);
          const pct = s && s.bytesRecorded ? Math.floor((s.bytesUploaded / s.bytesRecorded) * 100) : null;
          return (
            <div key={p.id} className="team-row">
              <div className="row between">
                <div className="who">
                  <Avatar name={p.name} size={28} />
                  <div className="col" style={{ gap: 0, minWidth: 0 }}>
                    <strong className="ellipsis">{p.name}</strong>
                    <span className="faint small">{roleLabel(p.role)}</span>
                  </div>
                </div>
                {s?.recording ? (
                  <span className="pill rec">
                    <span className="dot pulse" /> Recording
                  </span>
                ) : engine.recording ? (
                  <span className="pill bad">Not recording</span>
                ) : (
                  <span className="pill">Ready</span>
                )}
              </div>
              {s ? (
                <>
                  <div className="chips">
                    {s.sources.map((src) => (
                      <span
                        key={src.id}
                        className={`pill ${src.mute === 'hard' ? 'mute-hard' : src.mute === 'send' ? 'mute-send' : ''}`}
                        title={src.mute === 'hard' ? 'Hard-muted: not recorded' : src.mute === 'send' ? 'Not sent to the call, still recording' : src.detail}
                        style={src.mute === 'none' && !src.record ? { opacity: 0.6 } : undefined}
                      >
                        {src.label}
                        {src.mute === 'hard' ? ' · NOT recorded' : src.record ? ' · rec' : ''}
                        {src.mute === 'send' ? ' · not sent' : src.live ? ' · live' : ''}
                      </span>
                    ))}
                  </div>
                  <div className="facts">
                    {pct != null && <span>Uploaded {pct}% of {formatBytes(s.bytesRecorded)}</span>}
                    {s.uploadRateBps ? <span>{formatRate(s.uploadRateBps)}</span> : null}
                    {s.storageFreeBytes != null && <span>{formatBytes(s.storageFreeBytes)} free</span>}
                    {s.hardwareTier && <span style={{ textTransform: 'capitalize' }}>{s.hardwareTier} quality</span>}
                    {s.clockUncertaintyMs != null && <span>Sync ±{s.clockUncertaintyMs} ms</span>}
                    {meshPeer && <span>Live: {meshPeer.tier}</span>}
                    {s.cpuPressure && s.cpuPressure !== 'unknown' && s.cpuPressure !== 'nominal' && <span style={{ color: 'var(--warn)' }}>CPU {s.cpuPressure}</span>}
                  </div>
                  {s.warnings.map((w) => (
                    <div key={w} className="alert warn small">
                      <Icon name="warn" size={13} /> {w}
                    </div>
                  ))}
                </>
              ) : (
                <span className="faint small row">
                  <span className="spinner" style={{ width: 12, height: 12, borderWidth: 1.5 }} /> Waiting for status…
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="col" style={{ gap: 8 }}>
        <div className="section-title">
          <span className="eyebrow">Latest check</span>
          <button className="sm" onClick={() => engine.requestCheck()}>
            <Icon name="users" size={14} /> Check everyone
          </button>
        </div>
        {!latest ? (
          <p className="panel-note">
            Asks every participant’s app to replay its last 5 seconds from disk and send you the clips, each with an automatic
            verdict.
          </p>
        ) : (
          <div className="check-grid">
            {latest.map((c) => (
              <div key={c.id} className="col small" style={{ gap: 5 }}>
                {c.mime.startsWith('audio') ? (
                  <audio controls src={checkClipUrl(c.id, engine.key)} />
                ) : (
                  <video controls muted playsInline src={checkClipUrl(c.id, engine.key)} />
                )}
                <span className="ellipsis">
                  <strong>{c.participantName}</strong> <span className="muted">· {c.label}</span>
                </span>
                <span className={`pill ${c.verdict.ok ? 'ok' : 'bad'}`} title={c.verdict.summary}>
                  <Icon name={c.verdict.ok ? 'check' : 'warn'} size={12} /> <span className="ellipsis">{c.verdict.summary || 'Received'}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/** Hosts invite producers or guests by email: the link is emailed, and the studio shows in their list. */
function InviteByEmail({ engine }: { engine: StudioEngine }) {
  const [invites, setInvites] = useState<InviteInfo[] | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'producer' | 'guest'>('producer');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const load = () => void api.invites(engine.studioId, engine.key).then(setInvites).catch(() => {});
  useEffect(load, [engine.studioId]);
  const send = async () => {
    if (!isEmail(email) || busy) return setError(isEmail(email) ? '' : 'Enter a valid email address');
    setBusy(true);
    setError('');
    setNote('');
    try {
      const r = await api.invite(engine.studioId, engine.key, email.trim(), role);
      setNote(r.emailed ? `Invitation emailed to ${r.email}.` : `${r.email} is invited: send them the link yourself (this server doesn’t send email).`);
      setEmail('');
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="col" style={{ gap: 6 }}>
      <span className="eyebrow">Invite by email</span>
      <div className="invite-form">
        <input type="email" placeholder="name@example.com" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void send()} aria-label="Email to invite" />
        <select value={role} onChange={(e) => setRole(e.target.value as 'producer' | 'guest')} aria-label="Invite as">
          <option value="producer">Producer</option>
          <option value="guest">Guest</option>
        </select>
        <button onClick={() => void send()} disabled={busy || !email.trim()}>
          {busy && <span className="spinner" />} Invite
        </button>
      </div>
      <span className="faint small">They get the link by email, and the studio appears in their list when they sign in with that address.</span>
      {note && <span className="small" style={{ color: 'var(--ok)' }}>{note}</span>}
      {error && (
        <div className="alert bad small">
          <Icon name="warn" size={13} /> {error}
        </div>
      )}
      {invites?.map((i) => (
        <div key={i.email} className="invite-row">
          <Avatar name={i.email} size={22} />
          <span className="grow ellipsis">{i.email}</span>
          <span className="pill">{roleLabel(i.role)}</span>
          <button
            className="ghost sm icon"
            title="Remove invitation"
            aria-label={`Remove invitation for ${i.email}`}
            onClick={() => void api.removeInvite(engine.studioId, engine.key, i.email).then(load)}
          >
            <Icon name="close" size={13} />
          </button>
        </div>
      ))}
    </div>
  );
}
